import React, { useState } from 'react';
import Icon from 'components/AppIcon';

const MarketGuidelines = () => {
  const [expandedSection, setExpandedSection] = useState(null);

  const guidelineSections = [
    {
      id: 'best-practices',
      title: 'Best Practices',
      icon: 'CheckCircle',
      items: [
        'Ask clear, unambiguous questions',
        'Provide detailed context and background',
        'Set realistic and verifiable resolution criteria', 
        'Choose appropriate end dates',
        'Use relevant tags and categories'
      ]
    },
    {
      id: 'examples',
      title: 'Good Examples',
      icon: 'Lightbulb',
      items: [
        'Will Bitcoin close above $50,000 on December 31, 2024?',
        'Will SpaceX successfully land humans on Mars by 2030?',
        'Will the next iPhone release include a foldable screen?',
        'Will renewable energy exceed 50% of US power by 2025?'
      ]
    },
    {
      id: 'avoid',
      title: 'What to Avoid',
      icon: 'AlertTriangle',
      items: [
        'Vague or subjective questions',
        'Markets about illegal activities',
        'Questions with no clear resolution method',
        'Offensive or inappropriate content',
        'Markets that encourage harmful behavior'
      ]
    },
    {
      id: 'resolution',
      title: 'Resolution Tips',
      icon: 'Target',
      items: [
        'Specify exact data sources',
        'Define edge cases and contingencies',
        'Set clear thresholds and conditions',
        'Consider timezone for time-sensitive events',
        'Plan for potential delays or complications'
      ]
    }
  ];

  const successfulMarkets = [
    {
      title: 'Bitcoin $100K by 2024?',
      volume: '$2.5M',
      traders: '12.5K',
      category: 'Cryptocurrency'
    },
    {
      title: 'Election Winner 2024',
      volume: '$8.2M', 
      traders: '45.2K',
      category: 'Politics'
    },
    {
      title: 'Tesla Stock $400 in 2024?',
      volume: '$1.8M',
      traders: '8.7K', 
      category: 'Stocks'
    }
  ];

  const toggleSection = (sectionId) => {
    setExpandedSection(expandedSection === sectionId ? null : sectionId);
  };

  return (
    <div className="space-y-6">
      {/* Guidelines */}
      <div className="bg-surface border border-border rounded-trading shadow-trading-card p-6">
        <h3 className="font-body-semibold text-text-primary mb-4 flex items-center">
          <Icon name="BookOpen" size={16} className="mr-2 text-primary" />
          Market Guidelines
        </h3>
        
        <div className="space-y-3">
          {guidelineSections?.map((section) => (
            <div key={section?.id} className="border border-border rounded-trading">
              <button
                onClick={() => toggleSection(section?.id)}
                className="w-full flex items-center justify-between p-3 text-left hover:bg-surface-hover transition-colors duration-trading-fast"
              >
                <div className="flex items-center space-x-2">
                  <Icon name={section?.icon} size={14} className="text-primary" />
                  <span className="font-body-medium text-sm text-text-primary">{section?.title}</span>
                </div>
                <Icon 
                  name="ChevronDown" 
                  size={14} 
                  className={`text-text-secondary transition-transform duration-trading-fast ${
                    expandedSection === section?.id ? 'rotate-180' : ''
                  }`}
                />
              </button>
              
              {expandedSection === section?.id && (
                <div className="px-3 pb-3">
                  <ul className="space-y-1">
                    {section?.items?.map((item, index) => (
                      <li key={index} className="flex items-start space-x-2 text-xs text-text-secondary">
                        <Icon name="Dot" size={12} className="mt-0.5 text-primary" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Successful Markets Examples */}
      <div className="bg-surface border border-border rounded-trading shadow-trading-card p-6">
        <h3 className="font-body-semibold text-text-primary mb-4 flex items-center">
          <Icon name="TrendingUp" size={16} className="mr-2 text-success" />
          Successful Markets
        </h3>
        
        <div className="space-y-3">
          {successfulMarkets?.map((market, index) => (
            <div key={index} className="bg-surface-hover rounded-trading p-3">
              <div className="flex items-start justify-between mb-2">
                <h4 className="font-body-medium text-sm text-text-primary line-clamp-1">
                  {market?.title}
                </h4>
                <span className="text-xs bg-primary-900 text-primary-300 px-2 py-0.5 rounded">
                  {market?.category}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="flex items-center space-x-1">
                    <Icon name="DollarSign" size={12} className="text-success" />
                    <span className="text-xs text-text-secondary">{market?.volume}</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <Icon name="Users" size={12} className="text-primary" />
                    <span className="text-xs text-text-secondary">{market?.traders}</span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Quick Tips */}
      <div className="bg-primary-900 bg-opacity-20 border border-primary-800 rounded-trading p-4">
        <div className="flex items-start space-x-3">
          <Icon name="Info" size={16} className="text-primary mt-0.5" />
          <div>
            <p className="font-body-medium text-sm text-text-primary mb-2">Pro Tips</p>
            <ul className="space-y-1 text-xs text-text-secondary leading-relaxed">
              <li>• Markets with clear binary outcomes perform best</li>
              <li>• Include relevant links and sources in descriptions</li>
              <li>• Lower creator fees typically attract more traders</li>
              <li>• End dates 1-12 months out are most popular</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Support */}
      <div className="bg-surface-hover border border-border rounded-trading p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="font-body-medium text-sm text-text-primary">Need Help?</p>
            <p className="text-xs text-text-secondary">Get support creating your market</p>
          </div>
          <button className="flex items-center space-x-1 text-primary hover:text-primary-300 transition-colors duration-trading-fast">
            <Icon name="MessageCircle" size={14} />
            <span className="text-xs font-body-medium">Contact Support</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default MarketGuidelines;